/**
 * OAuthCallbackView — catches the browser redirect from social platforms.
 *
 * The platform redirects to /oauth/callback/:platform?code=xxx&state=yyy
 * This view extracts the code+state, sends them to the backend, then
 * shows success or error and redirects back to Settings.
 *
 * For Facebook — after success, shows a page selector so the user can
 * connect specific Pages they manage.
 */
import { useEffect, useState } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { CheckCircle, AlertCircle, Loader2, ExternalLink } from 'lucide-react'
import {
  submitOAuthCallback, listFacebookPages, connectFacebookPage,
  type FacebookPage,
} from '../api/client'

// ─── Platform display map ─────────────────────────────────────────────────────

const PLATFORM_META: Record<string, { label: string; color: string; icon: string }> = {
  x: { label: 'X (Twitter)', color: '#000000', icon: '𝕏' },
  linkedin: { label: 'LinkedIn', color: '#0A66C2', icon: 'in' },
  facebook: { label: 'Facebook', color: '#1877F2', icon: 'f' },
  instagram: { label: 'Instagram', color: '#E1306C', icon: 'IG' },
  tiktok: { label: 'TikTok', color: '#010101', icon: 'TT' },
  youtube: { label: 'YouTube', color: '#FF0000', icon: 'YT' },
  pinterest: { label: 'Pinterest', color: '#E60023', icon: 'P' },
  reddit: { label: 'Reddit', color: '#FF4500', icon: 'R' },
}

// ─── Facebook page selector ───────────────────────────────────────────────────

function FacebookPageSelector({
  accountId,
  onDone,
}: {
  accountId: string
  onDone: () => void
}) {
  const [pages, setPages] = useState<FacebookPage[]>([])
  const [userToken, setUserToken] = useState('')
  const [loading, setLoading] = useState(true)
  const [connecting, setConnecting] = useState<string | null>(null)
  const [connected, setConnected] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const qc = useQueryClient()

  useEffect(() => {
    listFacebookPages(accountId)
      .then(r => {
        setPages(r.pages)
        setUserToken(r.user_token)
      })
      .catch(e => setError((e as Error).message))
      .finally(() => setLoading(false))
  }, [accountId])

  const handleConnect = async (page: FacebookPage) => {
    setConnecting(page.id)
    try {
      await connectFacebookPage({
        page_id: page.id,
        page_name: page.name,
        access_token: userToken,
        category: page.category,
        avatar_url: page.picture?.data?.url,
      })
      setConnected(prev => new Set([...prev, page.id]))
      qc.invalidateQueries({ queryKey: ['connected-accounts'] })
      qc.invalidateQueries({ queryKey: ['integrations'] })
    } catch (e: unknown) {
      setError((e as Error).message)
    } finally {
      setConnecting(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-400 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading your Facebook Pages...
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-red-400 text-sm flex items-center gap-2">
        <AlertCircle className="w-4 h-4" />
        {error}
      </div>
    )
  }

  if (pages.length === 0) {
    return (
      <div className="text-slate-400 text-sm">
        No Facebook Pages found. Make sure you manage at least one Page.
        <button onClick={onDone} className="ml-3 text-brand-400 hover:text-brand-300 underline text-xs">
          Continue anyway →
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold text-slate-200">Select Pages to Connect</h4>
      <p className="text-xs text-slate-500">Choose which Facebook Pages you want SocialScale to manage:</p>
      <div className="space-y-2">
        {pages.map(page => (
          <div
            key={page.id}
            className="flex items-center gap-3 p-3 bg-slate-800 border border-slate-700 rounded-xl"
          >
            {page.picture?.data?.url ? (
              <img src={page.picture.data.url} alt={page.name}
                className="w-9 h-9 rounded-full object-cover shrink-0" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-blue-700 flex items-center justify-center text-white font-bold text-sm shrink-0">
                {page.name[0]}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-200 truncate">{page.name}</p>
              <p className="text-xs text-slate-500">{page.category}</p>
            </div>
            {connected.has(page.id) ? (
              <div className="flex items-center gap-1 text-green-400 text-xs">
                <CheckCircle className="w-4 h-4" />
                Connected
              </div>
            ) : (
              <button
                onClick={() => handleConnect(page)}
                disabled={connecting === page.id}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-700 hover:bg-blue-600
                           text-white text-xs font-medium transition-colors disabled:opacity-60"
              >
                {connecting === page.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                Connect
              </button>
            )}
          </div>
        ))}
      </div>
      <button
        onClick={onDone}
        className="w-full py-2 rounded-lg border border-slate-700 text-sm text-slate-400 hover:text-slate-300 hover:border-slate-600 transition-colors"
      >
        Done — go to Settings →
      </button>
    </div>
  )
}

// ─── Main callback view ───────────────────────────────────────────────────────

type State =
  | { type: 'loading' }
  | { type: 'success'; accountId: string; displayName: string; hasPages: boolean }
  | { type: 'error'; message: string }

export default function OAuthCallbackView() {
  const { platform } = useParams<{ platform: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [viewState, setViewState] = useState<State>({ type: 'loading' })
  const [showPageSelector, setShowPageSelector] = useState(false)

  const meta = PLATFORM_META[platform ?? ''] ?? { label: platform, color: '#666', icon: '?' }

  useEffect(() => {
    const code = searchParams.get('code')
    const state = searchParams.get('state')
    const error = searchParams.get('error')

    if (error) {
      setViewState({ type: 'error', message: `Authorization denied: ${searchParams.get('error_description') ?? error}` })
      return
    }

    if (!code || !state || !platform) {
      setViewState({ type: 'error', message: 'Missing authorization code or state parameter.' })
      return
    }

    submitOAuthCallback(platform, code, state)
      .then(r => {
        qc.invalidateQueries({ queryKey: ['connected-accounts'] })
        qc.invalidateQueries({ queryKey: ['integrations'] })
        const hasPages = (platform === 'facebook' || platform === 'instagram') && r.pages_hint != null
        setViewState({
          type: 'success',
          accountId: r.account.id,
          displayName: r.account.display_name,
          hasPages,
        })
        if (hasPages) setShowPageSelector(true)
      })
      .catch(e => {
        setViewState({ type: 'error', message: (e as Error).message })
      })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const goToSettings = () => navigate('/settings', { replace: true })

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-6">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg shadow-2xl">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-5 border-b border-slate-800">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm shrink-0"
            style={{ backgroundColor: meta.color }}
          >
            {meta.icon}
          </div>
          <div>
            <h2 className="text-base font-semibold text-white">
              {meta.label} Authorization
            </h2>
            <p className="text-xs text-slate-500">SocialScale OAuth callback</p>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {viewState.type === 'loading' && (
            <div className="flex flex-col items-center py-8 gap-4">
              <Loader2 className="w-8 h-8 animate-spin text-brand-400" />
              <p className="text-sm text-slate-400">Exchanging authorization code for tokens...</p>
              <p className="text-xs text-slate-600">This takes a moment</p>
            </div>
          )}

          {viewState.type === 'success' && (
            <div className="space-y-5">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-green-300">
                    {viewState.displayName} connected!
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {meta.label} account authorized successfully. Tokens are encrypted and stored securely.
                  </p>
                </div>
              </div>

              {showPageSelector && viewState.type === 'success' ? (
                <FacebookPageSelector
                  accountId={viewState.accountId}
                  onDone={goToSettings}
                />
              ) : (
                <button
                  onClick={goToSettings}
                  className="w-full py-2.5 rounded-xl bg-brand-600 hover:bg-brand-500 text-white text-sm font-medium transition-colors"
                >
                  Back to Settings →
                </button>
              )}
            </div>
          )}

          {viewState.type === 'error' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-300">Authorization failed</p>
                  <p className="text-xs text-slate-400 mt-1">{viewState.message}</p>
                </div>
              </div>

              <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 space-y-2">
                <p className="text-xs font-semibold text-slate-400">Common fixes:</p>
                <ul className="text-xs text-slate-500 space-y-1 list-disc list-inside">
                  <li>Make sure your App credentials are correct in SocialScale Settings</li>
                  <li>Check that your redirect URI matches what's configured in the platform's developer console</li>
                  <li>The OAuth state may have expired — restart the flow from Settings</li>
                  <li>Ensure your app is not in restricted mode</li>
                </ul>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={goToSettings}
                  className="flex-1 py-2 rounded-xl border border-slate-700 text-sm text-slate-400 hover:text-slate-300 transition-colors"
                >
                  Back to Settings
                </button>
                <a
                  href={PLATFORM_META[platform ?? '']?.label ? '#' : '#'}
                  onClick={e => { e.preventDefault(); goToSettings() }}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-brand-700 hover:bg-brand-600 text-white text-sm transition-colors"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Try again
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
